import React from 'react';
import Big from 'big.js';

import { clone } from '../utils/iterable/clone';

import { AMAZON, EBAY, ETSY, FAIRE, SHOPIFY, WOOCOMMERCE } from './channels';
import { MEDIA } from './socialMedia';
import { ICON } from './icons';

export const ACTIONS = {
  ADD_BACKGROUNDS: 'addBackgrounds',
  ADD_TO_HISTORY: 'addToHistory',
  BOOTSTRAP: 'bootstrap',
  LOAD_PHOTOS: 'loadPhotos',
  REDO_CHANGE: 'redoChange',
  SEARCH_UNSPLASH: 'searchUnsplash',
  SET_TOOL: 'setTool',
  SET_VALUE: 'setValue',
  UNDO_CHANGE: 'undoChange',
  UPLOAD_PHOTO: 'uploadPhoto',
};

export const TOOLS = {
  AI_EDIT: 'AI Edit & Cleanup',
  BACKGROUNDS: 'AI Backgrounds',
  CROP: 'AI Expand & Crop',
  ENHANCE: 'AI Enhance',
  LAYERS: 'Layers',
};

export const TOOL_TO_BUTTON_MAP = {
  [TOOLS.AI_EDIT]: ICON.AI_EDIT,
  [TOOLS.BACKGROUNDS]: ICON.REMOVE_BACKGROUND,
  [TOOLS.CROP]: ICON.CROP,
  [TOOLS.ENHANCE]: ICON.ENHANCE,
  [TOOLS.LAYERS]: ICON.LAYERS,
};

export const SAVE = {
  CURRENT: 'Update current photo',
  NEW: 'Save as new photo',
  REPLACE: 'Update listing photos',
  LIBRARY: 'Save to the Library',
};

export const CONTROLLERS = {
  DOWNLOAD_IMAGE: 'downloadImage',
  EXPAND: 'expand',
  ENHANCE: 'enhance',
  GENERATE_BACKGROUNDS: 'generateBackgrounds',
  GET_FILTER_PRESETS: 'getFilterPresets',
  ISOLATE: 'isolate',
  LOAD_BACKGROUNDS: 'loadBackgrounds',
  LOAD_LAYERS: 'loadLayers',
  SEARCH_UNSPLASH: 'searchUnsplash',
  SELECT_BACKGROUND: 'selectBackground',
  SELECT_COLOR: 'selectColor',
  SELECT_IMAGE_LAYER: 'selectImageLayer',
  SUGGEST_PROMPT: 'suggestPrompt',
  EDIT_IMAGE: 'editImage',
};

export const DIRECTION = {
  E: 'e',
  N: 'n',
  NE: 'ne',
  NW: 'nw',
  S: 's',
  SE: 'se',
  SW: 'sw',
  W: 'w',
};

export const AXIS = {
  X: 'x',
  Y: 'y',
};

export const DIMENSION = {
  HEIGHT: 'height',
  WIDTH: 'width',
};

export const AXIS_DIMENSION = {
  [AXIS.X]: DIMENSION.WIDTH,
  [AXIS.Y]: DIMENSION.HEIGHT,
};

export const DIMENSION_AXIS = {
  [DIMENSION.HEIGHT]: AXIS.Y,
  [DIMENSION.WIDTH]: AXIS.X,
};

export const OPPOSITE_DIMENSION = {
  [DIMENSION.HEIGHT]: DIMENSION.WIDTH,
  [DIMENSION.WIDTH]: DIMENSION.HEIGHT,
};

export const FILTER = {
  BLUR: 'Blur',
  BRIGHTNESS: 'Brightness',
  CLARITY: 'Clarity',
  CONTRAST: 'Contrast',
  EXPOSURE: 'Exposure',
  GAMMA: 'Gamma',
  HIGHLIGHTS: 'Highlights',
  SATURATION: 'Saturation',
  SHADOWS: 'Shadows',
};

export const BACKGROUND_MODE = {
  COLORS: 'Colors',
  PHOTOS: 'Photos',
};

export const CROP_MODE = {
  CROP: 'Crop',
  EXPAND: 'AI Expand',
};

export const EDIT_MODE = {
  EDIT: 'Edit',
  CLEANUP: 'Cleanup',
};

export const DEFAULT_FILTER_PRESETS = [
  // {
  //   name: 'Original',
  // },
  {
    name: 'Cream',
    values: {
      [FILTER.BRIGHTNESS]:  0,
      [FILTER.CLARITY]:     0,
      [FILTER.CONTRAST]:    -12,
      [FILTER.EXPOSURE]:    -4,
      [FILTER.GAMMA]:       0,
      [FILTER.HIGHLIGHTS]:  0,
      [FILTER.SATURATION]:  -36,
      [FILTER.SHADOWS]:     0,
    },
  },
  {
    name: 'Moody',
    values: {
      [FILTER.BRIGHTNESS]:  -17,
      [FILTER.CLARITY]:     -63,
      [FILTER.CONTRAST]:    17,
      [FILTER.EXPOSURE]:    -12,
      [FILTER.GAMMA]:       35,
      [FILTER.HIGHLIGHTS]:  -15,
      [FILTER.SATURATION]:  27,
      [FILTER.SHADOWS]:     13,
    },
  },
  {
    name: 'Retro',
    values: {
      [FILTER.BRIGHTNESS]:  5,
      [FILTER.CLARITY]:     27,
      [FILTER.CONTRAST]:    -1,
      [FILTER.EXPOSURE]:    0,
      [FILTER.GAMMA]:       -27,
      [FILTER.HIGHLIGHTS]:  13,
      [FILTER.SATURATION]:  -24,
      [FILTER.SHADOWS]:     0,
    },
  },
  {
    name: 'Detailed',
    values: {
      [FILTER.BRIGHTNESS]:  7,
      [FILTER.CLARITY]:     9,
      [FILTER.CONTRAST]:    0,
      [FILTER.EXPOSURE]:    -2,
      [FILTER.GAMMA]:       -50,
      [FILTER.HIGHLIGHTS]:  -4,
      [FILTER.SATURATION]:  0,
      [FILTER.SHADOWS]:     0,
    },
  },
  {
    name: 'Neat',
    values: {
      [FILTER.BRIGHTNESS]:  0,
      [FILTER.CLARITY]:     0,
      [FILTER.CONTRAST]:    1,
      [FILTER.EXPOSURE]:    0,
      [FILTER.GAMMA]:       -74,
      [FILTER.HIGHLIGHTS]:  0,
      [FILTER.SATURATION]:  17,
      [FILTER.SHADOWS]:     0,
    },
  },
  {
    name: 'Magic',
    values: {
      [FILTER.BRIGHTNESS]:  -4,
      [FILTER.CLARITY]:     38,
      [FILTER.CONTRAST]:    -7,
      [FILTER.EXPOSURE]:    0,
      [FILTER.GAMMA]:       8,
      [FILTER.HIGHLIGHTS]:  4,
      [FILTER.SATURATION]:  0,
      [FILTER.SHADOWS]:     -2,
    },
  },
];

export const DEFAULT_FILTERS = {
  [FILTER.BRIGHTNESS]: 0,
  [FILTER.CLARITY]: 0,
  [FILTER.CONTRAST]: 0,
  [FILTER.EXPOSURE]: 0,
  [FILTER.GAMMA]: 0,
  [FILTER.HIGHLIGHTS]: 0,
  [FILTER.SATURATION]: 0,
  [FILTER.SHADOWS]: 0,
};

export const DEFAULT_COLORS = [
  '#FFFFFF',
  '#000000',
  '#7DDABA',
  '#4164EA',
  '#1FFA0B',
  '#FF0000',
  '#962FBF',
];

export const DEFAULT_CROP_PRESETS = [
  {
    name: 'Basic presets',
    items: [
      {
        id: 'basic-square',
        name: 'Square',
        aspectRatio: 1,
        icon: ICON.SQUARE,
        label: '1:1',
      },
      {
        id: 'basic-landscape',
        name: 'Landscape',
        aspectRatio: new Big(16).div(9).toNumber(),
        icon: ICON.LANDSCAPE,
        label: '16:9',
      },
      {
        id: 'basic-portrait',
        name: 'Portrait',
        aspectRatio: new Big(3).div(4).toNumber(),
        icon: ICON.PORTRAIT,
        label: '3:4',
      },
    ],
  },
  {
    name: 'Channel presets',
    groups: [
      {
        name: 'Etsy',
        channel: ETSY,
        items: [
          {
            id: 'etsy-listing',
            name: 'Listing',
            dimensions: {
              width: 3000,
              height: 2250,
            },
            aspectRatio: new Big(3000).div(2250).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'etsy-thumbnail',
            name: 'Listing',
            dimensions: {
              width: 570,
              height: 456,
            },
            aspectRatio: new Big(570).div(456).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'etsy-shop',
            name: 'Shop icon',
            dimensions: {
              width: 400,
              height: 400,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'etsy-profile',
            name: 'Profile pic',
            dimensions: {
              width: 500,
              height: 500,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'etsy-team',
            name: 'Team logo',
            dimensions: {
              width: 170,
              height: 100,
            },
            aspectRatio: new Big(170).div(100).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'etsy-receipt',
            name: 'Receipt banner',
            dimensions: {
              width: 1600,
              height: 400,
            },
            aspectRatio: new Big(1600).div(400).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'etsy-big-shop',
            name: 'Big shop banner',
            dimensions: {
              width: 1600,
              height: 400,
            },
            aspectRatio: new Big(1600).div(400).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'etsy-mini-shop',
            name: 'Mini shop banner',
            dimensions: {
              width: 1600,
              height: 213,
            },
            aspectRatio: new Big(1600).div(213).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'etsy-carousel',
            name: 'Carousel banner',
            dimensions: {
              width: 1200,
              height: 300,
            },
            aspectRatio: new Big(1200).div(300).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'etsy-2-images',
            name: 'Collage banner (2 images)',
            dimensions: {
              width: 600,
              height: 300,
            },
            aspectRatio: new Big(600).div(300).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'etsy-4-images',
            name: 'Collage banner (4 images)',
            dimensions: {
              width: 400,
              height: 300,
            },
            aspectRatio: new Big(400).div(300).toNumber(),
            icon: ICON.LANDSCAPE,
          },
        ],
      },
      {
        name: 'Shopify',
        channel: SHOPIFY,
        items: [
          {
            id: 'shopify-product',
            name: 'Product image',
            dimensions: {
              width: 2048,
              height: 2048,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'shopify-banner',
            name: 'Banner image',
            dimensions: {
              width: 1200,
              height: 400,
            },
            aspectRatio: new Big(1200).div(400).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'shopify-hero',
            name: 'Hero image',
            dimensions: {
              width: 1280,
              height: 720,
            },
            aspectRatio: new Big(1280).div(720).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'shopify-collection',
            name: 'Collection image',
            dimensions: {
              width: 1024,
              height: 1024,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'shopify-header',
            name: 'Header image',
            dimensions: {
              width: 1200,
              height: 800,
            },
            aspectRatio: new Big(1200).div(800).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'shopify-slideshow',
            name: 'Slideshow banner',
            dimensions: {
              width: 2000,
              height: 600,
            },
            aspectRatio: new Big(2000).div(600).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'shopify-logo',
            name: 'Logo (square)',
            dimensions: {
              width: 100,
              height: 100,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
        ],
      },
      {
        name: 'Faire',
        channel: FAIRE,
        items: [
          {
            id: 'faire-main',
            name: 'Product shot',
            dimensions: {
              width: 2000,
              height: 2000,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'faire-cover',
            name: 'Cover photo',
            dimensions: {
              width: 4400,
              height: 1000,
            },
            aspectRatio: new Big(4400).div(1000).toNumber(),
            icon: ICON.LANDSCAPE,
          },
        ],
      },
      {
        name: 'eBay',
        channel: EBAY,
        items: [
          {
            id: 'ebay-product',
            name: 'Product image',
            dimensions: {
              width: 1600,
              height: 1600,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'ebay-minimum',
            name: 'Minimum image',
            dimensions: {
              width: 500,
              height: 500,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
        ],
      },
      {
        name: 'Woo',
        channel: WOOCOMMERCE,
        items: [
          {
            id: 'woo-product',
            name: 'Product images',
            dimensions: {
              width: 1000,
              height: 1000,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'woo-catalog-thumbnail',
            name: 'Catalog thumbnail',
            dimensions: {
              width: 300,
              height: 300,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'woo-gallery-thumbnail',
            name: 'Gallery thumbnail',
            dimensions: {
              width: 100,
              height: 100,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'woo-product-thumbnail',
            name: 'Product thumbnail ',
            dimensions: {
              width: 200,
              height: 200,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'woo-zoomable',
            name: 'Zoomable image',
            dimensions: {
              width: 1000,
              height: 1000,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
        ],
      },
      {
        name: 'Amazon',
        channel: AMAZON,
        items: [
          {
            id: 'amazon-product',
            name: 'Product image',
            dimensions: {
              width: 2000,
              height: 2000,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'amazon-ad',
            name: 'Ad',
            dimensions: {
              width: 1200,
              height: 1200,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'amazon-wide-ad',
            name: 'Wide ad',
            dimensions: {
              width: 1200,
              height: 628,
            },
            aspectRatio: new Big(1200).div(628).toNumber(),
            icon: ICON.LANDSCAPE,
          },
        ],
      },
    ],
  },
  {
    name: 'Social presets',
    groups: [
      {
        name: 'Facebook',
        media: MEDIA.FACEBOOK,
        items: [
          {
            id: 'facebook-square',
            name: 'Square',
            dimensions: {
              width: 1080,
              height: 1080,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'facebook-story',
            name: 'Story',
            dimensions: {
              width: 1080,
              height: 1920,
            },
            aspectRatio: new Big(1080).div(1920).toNumber(),
            icon: ICON.PORTRAIT,
          },
          {
            id: 'facebook-horizontal',
            name: 'Horizontal',
            dimensions: {
              width: 1200,
              height: 630,
            },
            aspectRatio: new Big(1200).div(630).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'facebook-event',
            name: 'Event cover',
            dimensions: {
              width: 1920,
              height: 1005,
            },
            aspectRatio: new Big(1920).div(1005).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'facebook-catalog',
            name: 'Catalog',
            dimensions: {
              width: 1024,
              height: 1024,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'facebook-avatar',
            name: 'Avatar',
            dimensions: {
              width: 1024,
              height: 1024,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
        ],
      },
      {
        name: 'Instagram',
        media: MEDIA.INSTAGRAM,
        items: [
          {
            id: 'instagram-story',
            name: 'Story',
            dimensions: {
              width: 1080,
              height: 1920,
            },
            aspectRatio: new Big(1080).div(1920).toNumber(),
            icon: ICON.PORTRAIT,
          },
          {
            id: 'instagram-square',
            name: 'Square',
            dimensions: {
              width: 1080,
              height: 1080,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'instagram-post',
            name: 'Post',
            dimensions: {
              width: 1080,
              height: 1350,
            },
            aspectRatio: new Big(1080).div(1350).toNumber(),
            icon: ICON.PORTRAIT,
          },
        ],
      },
      {
        name: 'Youtube',
        media: MEDIA.YOUTUBE,
        items: [
          {
            id: 'youtube-thumbnail',
            name: 'Thumbnail',
            dimensions: {
              width: 1280,
              height: 720,
            },
            aspectRatio: new Big(1280).div(720).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'youtube-profile',
            name: 'Profile',
            dimensions: {
              width: 1080,
              height: 1080,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'youtube-landscape',
            name: 'End screen',
            dimensions: {
              width: 1920,
              height: 1080,
            },
            aspectRatio: new Big(1920).div(1080).toNumber(),
            icon: ICON.LANDSCAPE,
          },
        ],
      },
      {
        name: 'X',
        media: MEDIA.X,
        items: [
          {
            id: 'x-header',
            name: 'Header',
            dimensions: {
              width: 1500,
              height: 500,
            },
            aspectRatio: 3,
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'x-post',
            name: 'Post',
            dimensions: {
              width: 1600,
              height: 900,
            },
            aspectRatio: new Big(1600).div(900).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'x-profile',
            name: 'Profile',
            dimensions: {
              width: 1080,
              height: 1080,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
        ],
      },
      {
        name: 'LinkedIn',
        media: MEDIA.LINKEDIN,
        items: [
          {
            id: 'linkedin-post',
            name: 'Post',
            dimensions: {
              width: 1920,
              height: 1080,
            },
            aspectRatio: new Big(1920).div(1080).toNumber(),
            icon: ICON.LANDSCAPE,
          },
          {
            id: 'linkedin-logo',
            name: 'Logo',
            dimensions: {
              width: 300,
              height: 300,
            },
            aspectRatio: 1,
            icon: ICON.SQUARE,
          },
          {
            id: 'linkedin-banner',
            name: 'Banner',
            dimensions: {
              width: 1128,
              height: 191,
            },
            aspectRatio: new Big(1128).div(191).toNumber(),
            icon: ICON.LANDSCAPE,
          },
        ],
      },
      {
        name: 'Pinterest',
        media: MEDIA.PINTEREST,
        items: [
          {
            id: 'pinterest-pin',
            name: 'Pin',
            dimensions: {
              width: 1000,
              height: 1500,
            },
            aspectRatio: new Big(1000).div(1500).toNumber(),
            icon: ICON.PORTRAIT,
          },
        ],
      },
    ],
  },
];

export const DEFAULT_UNSPLASH_IMAGES = {
  byId: {
    '6QPpKaxHhzU': {
      downloadLocation: 'https://api.unsplash.com/photos/6QPpKaxHhzU/download?ixid=M3wxMjA3fDB8MXxhbGx8fHx8fHx8fHwxNzU2OTc0NjU3fA',
      id: '6QPpKaxHhzU',
      name: 'modern-building-corner-with-dark-glass-windows',
      url: 'https://images.unsplash.com/photo-1756227584303-f1400daaa69d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMjA3fDB8MXxhbGx8fHx8fHx8fHwxNzU2OTc0NjU3fA&ixlib=rb-4.1.0&q=80&w=400',
    },
    'U34d_8zgMSk': {
      downloadLocation: 'https://api.unsplash.com/photos/U34d_8zgMSk/download?ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA',
      id: 'U34d_8zgMSk',
      name: 'starry-night-sky-over-silhouetted-mountains-and-trees',
      url: 'https://images.unsplash.com/photo-1756295630652-a6f81dfee205?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA&ixlib=rb-4.1.0&q=80&w=400',
    },
    'jt6QxZwSOCQ': {
      downloadLocation: 'https://api.unsplash.com/photos/jt6QxZwSOCQ/download?ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA',
      id: 'jt6QxZwSOCQ',
      name: 'palm-leaves',
      url: 'https://images.unsplash.com/photo-1525923838299-2312b60f6d69?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA&ixlib=rb-4.1.0&q=80&w=400',
    },
    '87ZpLG0mviA': {
      downloadLocation: 'https://api.unsplash.com/photos/87ZpLG0mviA/download?ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA',
      id: '87ZpLG0mviA',
      name: 'dense-green-forest-canopy-viewed-from-above',
      url: 'https://images.unsplash.com/photo-1756314891315-3295b50658f1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA&ixlib=rb-4.1.0&q=80&w=400',
    },
    'h6JfjzCKKyQ': {
      downloadLocation: 'https://api.unsplash.com/photos/h6JfjzCKKyQ/download?ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA',
      id: 'h6JfjzCKKyQ',
      name: 'tall-palm-trees-against-a-bright-sky',
      url: 'https://images.unsplash.com/photo-1756310652024-efa234eac5d9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA&ixlib=rb-4.1.0&q=80&w=400',
    },
    'lXnBpU7R0LM': {
      downloadLocation: 'https://api.unsplash.com/photos/lXnBpU7R0LM/download?ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA',
      id: 'lXnBpU7R0LM',
      name: 'snow-covered-landscape-with-frozen-water',
      url: 'https://images.unsplash.com/photo-1754922493956-364a7623a016?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA&ixlib=rb-4.1.0&q=80&w=400',
    },
  },
  ids: [
    '6QPpKaxHhzU',
    'U34d_8zgMSk',
    'jt6QxZwSOCQ',
    '87ZpLG0mviA',
    'h6JfjzCKKyQ',
    'lXnBpU7R0LM',
  ],
  total: 6,
};

export const LAYER_TYPE = {
  TEXT: 'Text',
  IMAGE: 'Image',
};

export const LAYER_ANCHORS = {
  [LAYER_TYPE.TEXT]: ['top-left', 'top-center', 'top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-center', 'bottom-right'],
  [LAYER_TYPE.IMAGE]: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
};

export const TEXT_ALIGN = {
  CENTER: 'Center',
  LEFT: 'Left',
  RIGHT: 'Right',
};

export const TEXT_DECORATION = {
  LINE_THROUGH: 'Line through',
  UNDERLINE: 'Underline',
};

export const FONT_FAMILY = {
  BARLOW: 'Barlow',
  CAVEAT_BRUSH: 'Caveat Brush',
  DELICIOUS_HANDRAWN: 'Delicious Handrawn',
  LATO: 'Lato',
  MERRIWEATHER: 'Merriweather',
  PACIFICO: 'Pacifico',
  PLAYFAIR: 'Playfair Display',
  SANS_SERIF: 'sans-serif',
  SATISFY: 'Satisfy',
  VELA_SANS: 'VelaSans',
  ZILLA_SLAB: 'Zilla Slab',
};

export const FONTS = [
  // FONT_FAMILY.VELA_SANS,
  FONT_FAMILY.SANS_SERIF,
  FONT_FAMILY.BARLOW,
  FONT_FAMILY.LATO,
  FONT_FAMILY.CAVEAT_BRUSH,
  FONT_FAMILY.DELICIOUS_HANDRAWN,
  FONT_FAMILY.SATISFY,
  FONT_FAMILY.PACIFICO,
  FONT_FAMILY.MERRIWEATHER,
  FONT_FAMILY.PLAYFAIR,
  FONT_FAMILY.ZILLA_SLAB,
];

export const FONT_NAME = {
  [FONT_FAMILY.BARLOW]: 'Barlow',
  [FONT_FAMILY.CAVEAT_BRUSH]: 'Caveat Brush',
  [FONT_FAMILY.DELICIOUS_HANDRAWN]: 'Delicious Handrawn',
  [FONT_FAMILY.LATO]: 'Lato',
  [FONT_FAMILY.MERRIWEATHER]: 'Merriweather',
  [FONT_FAMILY.PACIFICO]: 'Pacifico',
  [FONT_FAMILY.PLAYFAIR]: 'Playfair Display',
  [FONT_FAMILY.SANS_SERIF]: 'Sans Serif',
  [FONT_FAMILY.SATISFY]: 'Satisfy',
  [FONT_FAMILY.VELA_SANS]: 'Vela Sans',
  [FONT_FAMILY.ZILLA_SLAB]: 'Zilla Slab',
};

export const FONT_WEIGHT = {
  THIN: 100,
  EXTRA_LIGHT: 200,
  LIGHT: 300,
  REGULAR: 400,
  MEDIUM: 500,
  SEMI_BOLD: 600,
  BOLD: 700,
  EXTRA_BOLD: 800,
  BLACK: 900,
};

export const FONT_WEIGHT_LABEL = {
  [FONT_WEIGHT.THIN]: 'Thin',
  [FONT_WEIGHT.EXTRA_LIGHT]: 'Extra Light',
  [FONT_WEIGHT.LIGHT]: 'Light',
  [FONT_WEIGHT.REGULAR]: 'Regular',
  [FONT_WEIGHT.MEDIUM]: 'Medium',
  [FONT_WEIGHT.SEMI_BOLD]: 'Semi Bold',
  [FONT_WEIGHT.BOLD]: 'Bold',
  [FONT_WEIGHT.EXTRA_BOLD]: 'Extra Bold',
  [FONT_WEIGHT.BLACK]: 'Black',
};

export const FONT_WEIGHTS = {
  [FONT_FAMILY.BARLOW]: [FONT_WEIGHT.THIN, FONT_WEIGHT.REGULAR, FONT_WEIGHT.BOLD, FONT_WEIGHT.EXTRA_BOLD],
  [FONT_FAMILY.CAVEAT_BRUSH]: [FONT_WEIGHT.REGULAR],
  [FONT_FAMILY.DELICIOUS_HANDRAWN]: [FONT_WEIGHT.REGULAR],
  [FONT_FAMILY.LATO]: [FONT_WEIGHT.THIN, FONT_WEIGHT.REGULAR, FONT_WEIGHT.BOLD],
  [FONT_FAMILY.MERRIWEATHER]: [FONT_WEIGHT.REGULAR, FONT_WEIGHT.BOLD, FONT_WEIGHT.EXTRA_BOLD],
  [FONT_FAMILY.PACIFICO]: [FONT_WEIGHT.REGULAR],
  [FONT_FAMILY.PLAYFAIR]: [FONT_WEIGHT.REGULAR, FONT_WEIGHT.BOLD, FONT_WEIGHT.EXTRA_BOLD],
  [FONT_FAMILY.SANS_SERIF]: [FONT_WEIGHT.THIN, FONT_WEIGHT.REGULAR, FONT_WEIGHT.BOLD],
  [FONT_FAMILY.SATISFY]: [FONT_WEIGHT.REGULAR],
  [FONT_FAMILY.VELA_SANS]: [FONT_WEIGHT.THIN, FONT_WEIGHT.REGULAR, FONT_WEIGHT.BOLD],
  [FONT_FAMILY.ZILLA_SLAB]: [FONT_WEIGHT.REGULAR, FONT_WEIGHT.BOLD],
};

export const TEXT_BACKGROUNDS = [
  {
    icon: (
      <svg width="50" height="23" viewBox="0 0 50 23" fill="var(--color-jade-translucent)" stroke="var(--color-clover)" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.5" y="0.5" width="49" height="22" />
      </svg>
    ),
    sceneFunc(context, shape) {
      const width = shape.getAttr('width');
      const height = shape.getAttr('height');
      context.beginPath();
      context.rect(0, 0, width, height);
      context.closePath();
      context.fillStrokeShape(shape);
    },
  },
  {
    icon: (
      <svg width="50" height="23" viewBox="0 0 50 23" fill="var(--color-jade-translucent)" stroke="var(--color-clover)" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.5" y="0.5" rx="4.5" width="49" height="22" />
      </svg>
    ),
    sceneFunc(context, shape) {
      const width = shape.getAttr('width');
      const height = shape.getAttr('height');
      const radius = 0.2173913 * height;
      context.beginPath();
      context.roundRect(0, 0, width, height, radius);
      context.closePath();
      context.fillStrokeShape(shape);
    },
  },
  {
    icon: (
      <svg width="50" height="23" viewBox="0 0 50 23" fill="var(--color-jade-translucent)" stroke="var(--color-clover)" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.5" y="0.5" rx="11" width="49" height="22" />
      </svg>
    ),
    sceneFunc(context, shape) {
      const width = shape.getAttr('width');
      const height = shape.getAttr('height');
      const radius = height / 2;
      context.beginPath();
      context.roundRect(0, 0, width, height, radius);
      context.closePath();
      context.fillStrokeShape(shape);
    },
  },
  {
    icon: (
      <svg width="50" height="23" viewBox="0 0 50 23" fill="var(--color-jade-translucent)" stroke="var(--color-clover)" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.314.5h37.5l5.75 11-5.75 11h-37.5l-5.75-11 5.75-11Z" />
      </svg>
    ),
    sceneFunc(context, shape) {
      const width = shape.getAttr('width');
      const height = shape.getAttr('height');
      const sx = height / 8;
      const sy = height / 2;
      context.beginPath();
      context.moveTo(0, sy);
      context.lineTo(sx, 0);
      context.lineTo(width - sx, 0);
      context.lineTo(width, sy);
      context.lineTo(width - sx, height);
      context.lineTo(sx, height);
      context.closePath();
      context.fillStrokeShape(shape);
    },
  },
  {
    icon: (
      <svg width="50" height="23" viewBox="0 0 50 23" fill="var(--color-jade-translucent)" stroke="var(--color-clover)" xmlns="http://www.w3.org/2000/svg">
        <path d="M.8.5h48.4L44.5 11l4.7 10.5H.8l4.7-10L.8.5Z" />
      </svg>
    ),
    sceneFunc(context, shape) {
      const width = shape.getAttr('width');
      const height = shape.getAttr('height');
      const sx = height / 8;
      const sy = height / 2;
      context.beginPath();
      context.moveTo(0, 0);
      context.lineTo(width, 0);
      context.lineTo(width - sx, sy);
      context.lineTo(width, height);
      context.lineTo(0, height);
      context.lineTo(sx, sy);
      context.closePath();
      context.fillStrokeShape(shape);
    },
  },
  {
    icon: (
      <svg width="50" height="23" viewBox="0 0 50 23" fill="var(--color-jade-translucent)" stroke="var(--color-clover)" xmlns="http://www.w3.org/2000/svg">
        <path d="M48.5.5l-4 3 4 3.5-4 4 4 4-4 4 4 3.5h-47l4-3.5-4-4 4-4-4-4 4-3.5-4-3h47z" />
      </svg>
    ),
    sceneFunc(context, shape) {
      const width = shape.getAttr('width');
      const height = shape.getAttr('height');
      const sx = height / 8;
      const sy = sx;
      context.beginPath();
      context.moveTo(0, 0);
      context.lineTo(width, 0);
      context.lineTo(width - sx, sy);
      context.lineTo(width, sy * 2);
      context.lineTo(width - sx, sy * 3);
      context.lineTo(width, sy * 4);
      context.lineTo(width - sx, sy * 5);
      context.lineTo(width, sy * 6);
      context.lineTo(width - sx, sy * 7);
      context.lineTo(width, height);
      context.lineTo(0, height);
      context.lineTo(sx, sy * 7);
      context.lineTo(0, sy * 6);
      context.lineTo(sx, sy * 5);
      context.lineTo(0, sy * 4);
      context.lineTo(sx, sy * 3);
      context.lineTo(0, sy * 2);
      context.lineTo(sx, sy);
      context.closePath();
      context.fillStrokeShape(shape);
    },
  },
];

export const INITIAL_STATE = {
  backgrounds: {
    ai: {
      prompt: '',
    },
    blur: 0,
    photos: {},
    selectedBackground: null,
    selectedColor: null,
    standard: {
      mode: BACKGROUND_MODE.COLORS,
      colors: clone(DEFAULT_COLORS),
    },
  },
  aiEdit: {
    prompt: '',
    selectedImage: null,
    images: {},
    nodes: {},
    mode: EDIT_MODE.EDIT,
    brushSize: 30,
  },
  crop: {
    expandedImage: null,
    initialImage: null,
    mode: CROP_MODE.EXPAND,
    presets: clone(DEFAULT_CROP_PRESETS),
  },
  enhance: {
    enhancedImage: null,
    filters: clone(DEFAULT_FILTERS),
    initialImage: null,
    presets: clone(DEFAULT_FILTER_PRESETS),
  },
  history: { index: -1, items: [] },
  layers: {
    elements: {},
    image: {
      opacity: 1,
    },
    mode: LAYER_TYPE.TEXT,
    photos: {},
    text: {
      align: TEXT_ALIGN.LEFT,
      background: {
        enabled: false,
        color: '#DFF6EE',
        opacity: 1,
        shape: 0,
      },
      border: {
        enabled: false,
        color: '#009B8A',
        opacity: 1,
        width: '2',
      },
      color: '#000000',
      decoration: {},
      fontFamily: FONT_FAMILY.SANS_SERIF,
      fontWeight: FONT_WEIGHT.REGULAR,
      fontSize: '64',
      italic: false,
      letterSpacing: '0',
      lineHeight: '1.2',
    },
  },
  tools: {
    current: TOOLS.BACKGROUNDS,
    items: [
      TOOLS.BACKGROUNDS,
      TOOLS.CROP,
      TOOLS.ENHANCE,
      TOOLS.AI_EDIT,
      TOOLS.LAYERS,
    ],
  },
  unsplash: {
    images: clone(DEFAULT_UNSPLASH_IMAGES),
    page: 0,
    query: '',
  },
};
